import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Col from '../../components/Col';
import Row from '../../components/Row';
import Select from '../../components/Select';
import Picker from '../../components/Picker';
import {
  clearReports,
  fetchCustomerFeedback,
  fetchReports,
  fetchUserActivityReport,
  fetchUserStatusReports,
} from 'src/store/actions/serviceRequests';
import { Multiselect } from 'multiselect-react-dropdown';
import { CSVLink } from 'react-csv';
import FontAwesomeIcon from '../../components/Icon';
import ReportingTable from '../../components/ReportingTable/ReportingTable';
import Button from '../../components/Button';
import ReportingUserActivityTable from '../../components/ReportingTable/ReportingUserActivityTable';
import UserStatusReports from '../../components/ReportingTable/UserStatusReports';
import CustomerFeedbackReportTable from '../../components/ReportingTable/CustomerFeedbackTable';
import { ServiceRequestStatsForm } from './Forms/ServiceRequestStats';
import { ServiceRequestStatsReport } from './Forms/ServiceRequestStats/Report.jsx';
import { clearServiceRequestStats } from 'src/store/actions/serviceRequestStats';
import { ServiceRequestStatsReportSidebar } from './Forms/ServiceRequestStats/ReportSidebar.jsx';
import {
  OccupancyDeficiencyForm,
  SelectedProperty,
} from 'src/pages/Reporting/Forms/OccupancyDeficiencyForm/OccupancyDeficiencyForm';
import OccupancyDeficiencyTable from 'src/components/ReportingTable/OccupanyDeficiencyTable/OccupancyDeficiencyTable';
import PropertyAggregateTable from 'src/components/ReportingTable/PropertyAggregateTable';
import {
  OccupancyReportData,
  OccupancyReportParams,
  OccupancyReportResponse,
  IProperty,
  ReportingFormState,
  SelectedValue,
  StandardReportParams,
} from 'src/pages/Reporting/ReportingFormTypes';
import {
  customerFeedBackHeaders,
  headers,
  occupancyDeficiencyHeaders,
  types,
  userActivityHeaders,
  userStatusReportsHeaders,
} from 'src/pages/Reporting/constants';
import './Reporting.scss';
import {
  fetchOccupancyDeficiencyReport,
  prepareReportParams,
} from 'src/pages/Reporting/helpers';
import { LLMContainer } from 'src/components/LLM/LLMContainer';
import useProperties from 'src/utils/CommonDataFetchingUtils/useProperties';

interface PropertyOption {
  name: string;
  id: string;
}

interface PropertiesStatusProps {
  isLoading: boolean;
  error: string | null;
  propertiesCount: number;
}

const PropertiesStatus = ({
  isLoading,
  error,
  propertiesCount,
}: PropertiesStatusProps) => {
  if (isLoading) {
    return (
      <div
        className="properties-loading-message"
        style={{ margin: '10px 0', color: '#666' }}
      >
        Loading properties...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="properties-error-message"
        style={{ margin: '10px 0', color: 'red' }}
      >
        {error}
      </div>
    );
  }

  if (propertiesCount === 0 && !isLoading) {
    return (
      <div
        className="properties-empty-message"
        style={{ margin: '10px 0', color: '#666' }}
      >
        No properties available
      </div>
    );
  }

  return null;
};

interface DownloadReportButtonProps {
  data: any[];
  headers: { label: string; key: string }[];
  filename: string;
  title?: string; // Added optional title prop.
}

const DownloadReportButton = ({
  data,
  headers,
  filename,
  title = 'Download Report', // Default value if no title provided.
}: DownloadReportButtonProps) => {
  if (!data || data.length === 0) return null;

  return (
    <div style={{ justifyContent: 'flex-end', display: 'flex', margin: 5 }}>
      <CSVLink
        data={data}
        asyncOnClick={true}
        headers={headers}
        separator={','}
        filename={filename}
      >
        <div style={{ justifyContent: 'flex-end', display: 'flex', margin: 5 }}>
          <div style={{ paddingRight: 10 }}>{title}</div>
          <FontAwesomeIcon glyph="cloud-download" weight="regular" size="big" />
        </div>
      </CSVLink>
    </div>
  );
};

interface ReportHeader {
  label: string;
  key: string;
}

const ReportingForm = () => {
  // General component state.
  const [state, setState] = useState<ReportingFormState>({
    propertyId: '',
    selectedValues: [],
    fromDate: null,
    toDate: new Date(),
    type: 'Reporting',
  });

  // Local state for occupancy deficiency report.
  const [occupancyReportData, setOccupancyReportData] = useState<
    OccupancyReportData[]
  >([]);
  const [propertyAggregateReportData, setPropertyAggregateReportData] =
    useState<OccupancyReportData[]>();
  const [isLoadingOccupancyReport, setIsLoadingOccupancyReport] =
    useState<boolean>(false);

  // State to keep track of the multiple properties the user has selected in the UI of the pre vs post deficiency report.
  const [selectedProperties, setSelectedProperties] = useState<
    SelectedProperty[]
  >([]);

  const reports = useSelector((state: any) => state.reports);
  const feedbackReport = useSelector((state: any) => state.feedbackReport);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearReports());
    dispatch(clearServiceRequestStats());
    setOccupancyReportData([]);
  }, [dispatch]);

  const {
    data: properties,
    loading: isLoadingProperties,
    error: propertiesError,
  } = useProperties();

  const propertyOptions: PropertyOption[] = useMemo(() => {
    return (
      properties?.map((property: IProperty) => ({
        name: property.name,
        id: property.id,
      })) || []
    );
  }, [properties]);

  const onSelect = (selectedList: SelectedValue[]): void => {
    setState({
      ...state,
      selectedValues: selectedList,
    });
  };

  const onRemove = (selectedList: SelectedValue[]): void => {
    setState({
      ...state,
      selectedValues: selectedList,
    });
  };

  const updateSelectedProperties = (selectedList: SelectedProperty[]) => {
    setSelectedProperties(selectedList);
  };

  const generateReport = (): void => {
    const params: string | StandardReportParams = prepareReportParams(
      state.type,
      state.selectedValues,
      state.fromDate,
      state.toDate
    );

    // Handle dispatch based on a report type.
    switch (state.type) {
      case 'User Status Report':
        dispatch(fetchUserStatusReports(params as string));
        break;
      case 'Customer Feedback Report':
        dispatch(fetchCustomerFeedback(params));
        break;
      case 'Reporting':
        dispatch(clearReports());
        dispatch(fetchReports(params));
        break;
      case 'User Activity':
        dispatch(clearReports());
        dispatch(fetchUserActivityReport(params));
        break;
      default:
        console.warn(`Unhandled report type: ${state.type}`);
        break;
    }
  };

  const handleGenerateOccupancyReport = async (propertyId?: string) => {
    setIsLoadingOccupancyReport(true);

    try {
      const listOfSelectedPropertyIds: string[] = selectedProperties.map(
        (property: SelectedProperty) => {
          return property.id;
        }
      );

      const params: OccupancyReportParams = {
        listOfSelectedPropertyIds,
      };

      const response: OccupancyReportResponse =
        await fetchOccupancyDeficiencyReport(params);
      setOccupancyReportData(response.data.unitAggregates);
      setPropertyAggregateReportData(response.data.propertyAggregates);
    } catch (error) {
      console.error('Error fetching occupancy report:', error);
    } finally {
      setIsLoadingOccupancyReport(false);
    }
  };

  const reportHeaders: ReportHeader[] =
    state.type === 'Reporting'
      ? headers
      : state.type === 'User Status Report'
        ? userStatusReportsHeaders
        : state.type === 'Customer Feedback Report'
          ? userStatusReportsHeaders
          : state.type === 'OccupancyDeficiencyReport'
            ? occupancyDeficiencyHeaders
            : userActivityHeaders;

  const fileName: string =
    state.type === 'User Status Report'
      ? 'UserStatusReport.csv'
      : state.type === 'OccupancyDeficiencyReport'
        ? 'OccupancyDeficiencyReport.csv'
        : 'Report.csv';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Row style={{ flex: '1 1 auto' }}>
        <Col xs="12" md="6">
          <Col xs="12" md="12">
            <Select
              value={state.type}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                setState({
                  ...state,
                  type: e.target.value,
                });
                dispatch(clearReports());
                dispatch(clearServiceRequestStats());
                setOccupancyReportData([]);
                setPropertyAggregateReportData([]);
              }}
              options={types.map((item: any) => ({
                label: item.label,
                value: item.value,
              }))}
              label="Select Reporting Type"
              placeholder="Select Reporting Type"
              block
            />
            {state.type === 'Service Request Report' ? (
              <ServiceRequestStatsForm properties={properties} />
            ) : state.type === 'OccupancyDeficiencyReport' ? (
              <OccupancyDeficiencyForm
                properties={properties}
                onGenerateReport={handleGenerateOccupancyReport}
                selectedProperties={selectedProperties}
                updateSelectedProperties={updateSelectedProperties}
              />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="reporting__heading">SELECT PROPERTIES</div>
                  {state.type !== 'User Status Report' && (
                    <div className="picker__required" />
                  )}
                </div>
                <PropertiesStatus
                  isLoading={isLoadingProperties}
                  error={propertiesError}
                  propertiesCount={properties?.length}
                />
                <Multiselect
                  options={propertyOptions}
                  displayValue="name"
                  selectedValues={state.selectedValues}
                  placeholder="Select Property"
                  onSelect={onSelect}
                  onRemove={onRemove}
                />
                {state.type !== 'User Status Report' && (
                  <>
                    <div className="reporting__heading">
                      SELECT REPORT DATES
                    </div>
                    <Picker
                      label="From Date"
                      required={true}
                      showPopperArrow={false}
                      placeholderText="From Date"
                      selected={state.fromDate}
                      onChange={(date: Date | null) =>
                        setState({
                          ...state,
                          fromDate: date,
                        })
                      }
                    />
                    <Picker
                      label="To Date"
                      required={true}
                      showPopperArrow={false}
                      placeholderText="To Date"
                      selected={state.toDate}
                      onChange={(date: Date | null) =>
                        setState({
                          ...state,
                          toDate: date as Date,
                        })
                      }
                    />
                  </>
                )}
                <div style={{ marginBottom: 20 }} />
                {state.type !== 'Customer Feedback Report' && (
                  <Button
                    color="teal"
                    size="small"
                    label="Generate Report"
                    disabled={
                      state.type === 'User Status Report'
                        ? false
                        : !state.selectedValues.length || !state.fromDate
                    }
                    onClick={() => generateReport()}
                  />
                )}
                {state.type === 'Customer Feedback Report' && (
                  <Button
                    color="teal"
                    size="small"
                    label="Generate Report"
                    disabled={!state.toDate || !state.fromDate}
                    onClick={() => generateReport()}
                  />
                )}
              </>
            )}
          </Col>
        </Col>
        <Col xs="12" md="6">
          <LLMContainer />
        </Col>
      </Row>
      <Row style={{ marginTop: '3rem' }}>
        <Col xs="12" md="12">
          <div
            style={{
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginRight: 20,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {state.type === 'OccupancyDeficiencyReport' ? (
              <>
                <DownloadReportButton
                  data={propertyAggregateReportData}
                  headers={occupancyDeficiencyHeaders}
                  filename="OccupancyDeficiencyReport_ByProject.csv"
                  title="Download Stats by Project Report"
                />
                <DownloadReportButton
                  data={occupancyReportData}
                  headers={occupancyDeficiencyHeaders}
                  filename="OccupancyDeficiencyReport_ByUnit.csv"
                  title="Download Stats by Units Report"
                />
              </>
            ) : state.type === 'Customer Feedback Report' ? (
              <DownloadReportButton
                data={feedbackReport}
                headers={customerFeedBackHeaders}
                filename="CustomerFeedbackReport.csv"
                title="Download Customer Feedback Data"
              />
            ) : (
              <DownloadReportButton
                data={reports}
                headers={reportHeaders}
                filename={fileName}
                title={`Download ${state.type} Data`}
              />
            )}
            {state.type === 'Service Request Report' && (
              <ServiceRequestStatsReportSidebar />
            )}
          </div>
        </Col>
        {/* Bottom Tables */}
        <div>
          {state.type === 'OccupancyDeficiencyReport' && (
            <>
              <PropertyAggregateTable
                data={propertyAggregateReportData}
                isLoading={isLoadingOccupancyReport}
              />
              <OccupancyDeficiencyTable
                reports={occupancyReportData}
                isLoading={isLoadingOccupancyReport}
              />
            </>
          )}
          {state.type === 'Reporting' && (
            <ReportingTable reports={reports} from="Property" />
          )}
          {state.type === 'User Activity' && (
            <ReportingUserActivityTable reports={reports} from="Property" />
          )}
          {state.type === 'User Status Report' && (
            <UserStatusReports reports={reports} />
          )}
          {state.type === 'Customer Feedback Report' && (
            <CustomerFeedbackReportTable reports={feedbackReport} />
          )}
          {state.type === 'Service Request Report' && (
            <ServiceRequestStatsReport />
          )}
        </div>
      </Row>
    </div>
  );
};

export default ReportingForm;
